import React, { useState, useEffect } from 'react';
import { PropagateLoader } from 'react-spinners';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';

import { FaAngleDown, FaFileAlt } from 'react-icons/fa';

import Paginator from '~/components/Paginator';
import PrevButton from '~/components/Paginator/PrevButton';
import NextButton from '~/components/Paginator/NextButton';
import CenterButton from '~/components/Paginator/CenterButton';
import StoreRelationshipDisplayDetailModal from '~/components/Modals/StoreRelationships/DisplayDetail';

import Input from '~/components/Input';

import api from '~/services/api';

import {
  Container,
  Label,
  Wrapper,
  Table,
  Th,
  Td,
  DetailBtn,
  ButtonsContent,
} from './styles';

interface IDisplaysProps {
  store_relationship_id: string;
}

interface IStoreRelationshipDisplay {
  id: string;
  completion_date: Date;
  record_type: string;
  note: string;
  display: {
    code: string;
    name: string;
  };
  store_relationship: {
    code: string;
  };
  user_inclusion: {
    name: string;
  };
  user_update: {
    name: string;
  };
  created_at: string;
  updated_at: string;
}

const Displays: React.FC<IDisplaysProps> = ({ store_relationship_id }) => {
  const [page, setPage] = useState(0);
  const [pageFilter, setPageFilter] = useState(0);
  const [filter, setFilter] = useState('');
  const [filterHelper, setFilterHelper] = useState('');
  const [refresh, setRefresh] = useState(true);
  const [storeRelationshipDisplays, setStoreRelationshipDisplays] =
    useState<IStoreRelationshipDisplay[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [nextDisabled, setNextDisabled] = useState(true);
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [storeRelationshipCodeDetail, setStoreRelationshipCodeDetail] =
    useState('');
  const [idDetail, setIdDetail] = useState('');
  const [completionDateDetail, setCompletionDateDetail] = useState(new Date());
  const [typeDetail, setTypeDetail] = useState('');
  const [recordTypeDetail, setRecordTypeDetail] = useState('');
  const [noteDetail, setNoteDetail] = useState('');
  const [createdAtDetail, setCreatedAtDetail] = useState(new Date());
  const [updatedAtDetail, setUpdatedAtDetail] = useState(new Date());
  const [userInclusionDetail, setUserInclusionDetail] = useState('');
  const [userUpdateDetail, setUserUpdateDetail] = useState('');

  useEffect(() => {
    if (refresh && store_relationship_id) {
      setRefresh(false);
      let pageSearch = 0;
      let foundData = false;

      if (filter !== filterHelper) {
        setPage(0);
        setPageFilter(0);
      }

      setFilterHelper(filter);

      if (filter !== '') {
        pageSearch = pageFilter;
      } else {
        pageSearch = page;
      }

      api
        .get(
          `/storerelationshipdisplays/storerelationship` +
            `?store_relationship_id=${store_relationship_id}` +
            `&page=${pageSearch}` +
            `&filter=${filter}`,
        )
        .then(firstResponse => {
          if (firstResponse.data.length > 0) {
            foundData = true;
            setStoreRelationshipDisplays(firstResponse.data);
            setLoading(false);
          }

          if (!foundData) {
            if (pageSearch - 1 >= 0) {
              pageSearch -= 1;
              if (filter !== '') {
                setPageFilter(pageFilter - 1);
              } else {
                setPage(page - 1);
              }
            }

            api
              .get(
                `/storerelationshipdisplays/storerelationship` +
                  `?store_relationship_id=${store_relationship_id}` +
                  `&page=${pageSearch}` +
                  `&filter=${filter}`,
              )
              .then(secondResponse => {
                setStoreRelationshipDisplays(secondResponse.data);
                setLoading(false);

                api
                  .get(
                    `/storerelationshipdisplays/storerelationship` +
                      `?store_relationship_id=${store_relationship_id}` +
                      `&page=${pageSearch + 1}` +
                      `&filter=${filter}`,
                  )
                  .then(thirdResponse => {
                    setNextDisabled(false);
                    if (thirdResponse.data.length === 0) {
                      setNextDisabled(true);
                    }
                  });
              });
          } else {
            api
              .get(
                `/storerelationshipdisplays/storerelationship` +
                  `?store_relationship_id=${store_relationship_id}` +
                  `&page=${pageSearch + 1}` +
                  `&filter=${filter}`,
              )
              .then(fourthResponse => {
                setNextDisabled(false);
                if (fourthResponse.data.length === 0) {
                  setNextDisabled(true);
                }
              });
          }
        });
    }
  }, [page, pageFilter, filter, filterHelper, refresh, store_relationship_id]);

  function handlePage(type: string): void {
    if (type === 'backward') {
      if (filter !== '') {
        setPageFilter(pageFilter - 1);
      } else {
        setPage(page - 1);
      }
    } else if (type === 'forward') {
      if (filter !== '') {
        setPageFilter(pageFilter + 1);
      } else {
        setPage(page + 1);
      }
    } else if (filter !== '') {
      setPageFilter(0);
    } else {
      setPage(0);
    }
    setRefresh(true);
  }

  function handlePrevButtonDisabled(): boolean {
    let validationPage = 0;

    if (filter !== '') {
      validationPage = pageFilter;
    } else {
      validationPage = page;
    }

    if (validationPage === 0) {
      return true;
    }

    return false;
  }

  function handleFilterChange(value: string): void {
    setFilter(value);
    setRefresh(true);
  }

  function handleTable(): React.ReactNode {
    if (storeRelationshipDisplays) {
      if (storeRelationshipDisplays.length > 0) {
        return (
          <Table>
            <thead>
              <tr>
                <Th width="30%" textAlign="left">
                  Data Conclusão
                </Th>
                <Th width="30%" textAlign="left">
                  Tipo de Display
                </Th>
                <Th width="30%" textAlign="left">
                  Tipo de Entrada
                </Th>
                <Th width="10%" textAlign="center" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <Td colSpan={4} padding="1rem" textAlign="center">
                    <PropagateLoader color="#276c8d" />
                  </Td>
                </tr>
              )}
              {storeRelationshipDisplays?.map(storeRelationshipDisplay => (
                <tr key={storeRelationshipDisplay.id}>
                  <Td width="30%" textAlign="left" heading="DATA CONCLUSÃO">
                    {new Date(
                      storeRelationshipDisplay.completion_date,
                    ).toLocaleDateString()}
                  </Td>
                  <Td width="30%" textAlign="left" heading="TIPO DE DISPLAY">
                    {`(${storeRelationshipDisplay.display.code}) - ${storeRelationshipDisplay.display.name}`}
                  </Td>
                  <Td width="30%" textAlign="left" heading="TIPO DE ENTRADA">
                    {storeRelationshipDisplay.record_type}
                  </Td>
                  <Td
                    width="10%"
                    textAlign="center"
                    display="flex"
                    heading={' '}
                  >
                    <DetailBtn
                      onClick={() => {
                        setIsOpenDetail(true);
                        setStoreRelationshipCodeDetail(
                          storeRelationshipDisplay.store_relationship.code,
                        );
                        setIdDetail(storeRelationshipDisplay.id);
                        setCompletionDateDetail(
                          new Date(storeRelationshipDisplay.completion_date),
                        );
                        setTypeDetail(
                          `(${storeRelationshipDisplay.display.code})` +
                            ` - ${storeRelationshipDisplay.display.name}`,
                        );
                        setRecordTypeDetail(
                          storeRelationshipDisplay.record_type,
                        );
                        setNoteDetail(storeRelationshipDisplay.note);
                        setCreatedAtDetail(
                          new Date(storeRelationshipDisplay.created_at),
                        );
                        setUpdatedAtDetail(
                          new Date(storeRelationshipDisplay.updated_at),
                        );
                        setUserInclusionDetail(
                          storeRelationshipDisplay.user_inclusion.name,
                        );
                        setUserUpdateDetail(
                          storeRelationshipDisplay.user_update.name,
                        );
                      }}
                    >
                      <FaFileAlt size={22} />
                    </DetailBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        );
      }
    }

    return (
      <Table>
        <thead>
          <tr>
            <Th width="100%" textAlign="center" />
          </tr>
        </thead>
        <tbody>
          <tr>
            <Td width="100%" textAlign="center">
              Nenhuma informação disponível
            </Td>
          </tr>
        </tbody>
      </Table>
    );
  }

  function clearStoreRelationshipDisplayDetail(): void {
    setStoreRelationshipCodeDetail('');
    setIdDetail('');
    setCompletionDateDetail(new Date());
    setTypeDetail('');
    setRecordTypeDetail('');
    setNoteDetail('');
    setCreatedAtDetail(new Date());
    setUpdatedAtDetail(new Date());
    setUserInclusionDetail('');
    setUserUpdateDetail('');
  }

  if (!store_relationship_id) {
    return null;
  }

  return (
    <Container>
      <Accordion>
        <AccordionSummary expandIcon={<FaAngleDown />}>
          <Label>Displays</Label>
        </AccordionSummary>

        <AccordionDetails>
          <Wrapper>
            <form onSubmit={() => {}}>
              <div
                style={{
                  display: 'flex',
                  maxWidth: 680,
                }}
              >
                <Input
                  name="search"
                  labelText="Pesquisar"
                  type="text"
                  onChange={event => handleFilterChange(event.target.value)}
                />
              </div>
            </form>

            {handleTable()}

            <ButtonsContent>
              <Paginator>
                <PrevButton
                  disabled={handlePrevButtonDisabled()}
                  onClick={() => handlePage('backward')}
                />
                <CenterButton onClick={() => handlePage('reset')} />
                <NextButton
                  disabled={nextDisabled}
                  onClick={() => handlePage('forward')}
                />
              </Paginator>
            </ButtonsContent>
          </Wrapper>
        </AccordionDetails>
      </Accordion>

      <StoreRelationshipDisplayDetailModal
        isOpen={isOpenDetail}
        store_relationship_code={storeRelationshipCodeDetail}
        store_relationship_display_id={idDetail}
        completion_date={completionDateDetail}
        type={typeDetail}
        record_type={recordTypeDetail}
        note={noteDetail}
        created_at={createdAtDetail}
        updated_at={updatedAtDetail}
        user_inclusion={userInclusionDetail}
        user_update={userUpdateDetail}
        setIsOpen={setIsOpenDetail}
        onClose={() => {
          clearStoreRelationshipDisplayDetail();
        }}
      />
    </Container>
  );
};

export default Displays;
